import React, { useEffect, useState, Suspense } from "react";
import { Spinner } from "react-bootstrap";

import Sidebar, { SidebarCompany, SidebarUser } from "./Sidebar";
import Topbar, { BreadcrumbItem } from "./Topbar";
import { NAV_SECTIONS, NavSection } from "./nav-config";
import { PortalChromeProvider, usePortalChromeState } from "./PortalChrome";
import styles from "./PortalLayout.module.scss";

// Shown in the content panel while a lazy page chunk loads. The route components are
// code-split (React.lazy), so navigating to one suspends — without this boundary React
// throws "A component suspended while responding to synchronous input" on every nav.
const ContentLoader = () => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 240,
      padding: "2rem",
    }}
  >
    <Spinner
      animation="border"
      role="status"
      style={{ width: "2rem", height: "2rem", color: "#b8c0c4" }}
    >
      <span className="visually-hidden">Loading...</span>
    </Spinner>
  </div>
);

// =============================================================================
// PortalLayout — Lumiere Ivory app shell
// -----------------------------------------------------------------------------
// A self-contained, two-panel floating layout (sidebar + content panel) for the
// theme migration. It carries NO dependency on the legacy Hyper machinery — no
// redux Layout slice, no `data-*` html attributes. Its only externals are React
// and react-router-dom, so it ports cleanly into the admin portal.
//
// Usage:
//   <PortalLayout
//     company={{ name: "Jolez Inc." }}
//     user={{ name: "Adam Smith", email: "adam@jolez.com" }}
//     breadcrumb={[{ label: "Requests", to: "/supplier/available-requests" },
//                  { label: "Available Requests" }]}
//     updatedLabel="Updated 2 min ago"
//   >
//     <PageContent />
//   </PortalLayout>
// =============================================================================

export interface PortalLayoutProps {
  children: React.ReactNode;
  company: SidebarCompany;
  user: SidebarUser;
  /** Breadcrumb trail for the topbar. */
  breadcrumb?: BreadcrumbItem[];
  /** Override the sidebar nav. Defaults to NAV_SECTIONS. */
  nav?: NavSection[];
  /** Custom right-side topbar actions; falls back to the Reload/Updated/Notifications cluster. */
  topbarActions?: React.ReactNode;
  // default-actions passthrough
  updatedLabel?: string;
  onReload?: () => void;
  onNotifications?: () => void;
  notificationCount?: number;
  /** Live notifications control for the topbar (see Topbar `bell`). */
  bell?: React.ReactNode;
  /** Footer CTA button. Pass `null` to hide it (admin portal). */
  createCta?: { label: string; to: string } | null;
  logoutTo?: string;
}

// The shell consumes the chrome context, so it lives below the provider.
const PortalShell = ({
  children,
  company,
  user,
  breadcrumb = [],
  nav = NAV_SECTIONS,
  topbarActions,
  updatedLabel,
  onReload,
  onNotifications,
  notificationCount,
  bell,
  createCta,
  logoutTo,
}: PortalLayoutProps) => {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const closeDrawer = () => setDrawerOpen(false);

  // A page can override the topbar chrome via usePortalChrome(); fall back to props.
  const { breadcrumb: ctxBreadcrumb, topbarActions: ctxActions } = usePortalChromeState();
  const effectiveBreadcrumb = ctxBreadcrumb ?? breadcrumb;
  const effectiveActions = ctxActions ?? topbarActions;

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [drawerOpen]);

  return (
    <div className={`${styles.shell}${drawerOpen ? ` ${styles.shellDrawerOpen}` : ""}`}>
      <aside className={styles.sidebarPanel}>
        <Sidebar
          company={company}
          user={user}
          nav={nav}
          createCta={createCta}
          logoutTo={logoutTo}
          onNavigate={closeDrawer}
        />
      </aside>

      {/* Backdrop for the mobile drawer */}
      <div
        className={styles.backdrop}
        onClick={closeDrawer}
        aria-hidden="true"
      />

      <main className={styles.contentPanel}>
        <Topbar
          breadcrumb={effectiveBreadcrumb}
          onMenuToggle={() => setDrawerOpen((v) => !v)}
          actions={effectiveActions}
          updatedLabel={updatedLabel}
          onReload={onReload}
          onNotifications={onNotifications}
          notificationCount={notificationCount}
          bell={bell}
        />
        <section className={styles.content}>
          <Suspense fallback={<ContentLoader />}>{children}</Suspense>
        </section>
      </main>
    </div>
  );
};

const PortalLayout = (props: PortalLayoutProps) => (
  <PortalChromeProvider>
    <PortalShell {...props} />
  </PortalChromeProvider>
);

export default PortalLayout;
